import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Pencil, User, Image as ImageIcon, Type, Check, Loader, AlertTriangle,
    LayoutList, Link as LinkIcon,
    Palette, Smartphone, Brush,
    Trash2, PlusCircle, GripVertical, AlignLeft, AlignCenter, AlignRight,
    MapPin, FileText, Instagram, X as LucideX, // X 아이콘 이름 충돌 방지
    Copy, ExternalLink,
    Plus, Music2, Sparkles, BarChart3, History as HistoryIcon,
    Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import { AnimatePresence, motion, Reorder, useDragControls } from 'framer-motion';
import ProfilePreview from '../components/ProfilePreview';
import ShareModal from '../components/ShareModal';
import ServerCardStudio from './ServerCardStudio';
import Cropper from 'react-easy-crop';
import { getLocaleFromPathname } from '../utils/locale';
import {
    FaInstagram,
    FaYoutube,
    FaXTwitter,
    FaDiscord,
    FaTiktok, // 예시로 추가
    FaFacebook, // 예시로 추가
    FaSpotify
} from 'react-icons/fa6';

// --- 유틸리티 훅 ---
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
}

const getImageUrl = (filename) => {
    if (!filename) return null;
    if (filename.startsWith('blob:')) return filename;
    return `/uploads/${filename}`;
};

const generateId = () => Math.random().toString(36).substr(2, 9);

// --- [신규] SNS 플랫폼 정의 ---
const SNS_PLATFORMS = {
    instagram: { icon: <FaInstagram className="w-5 h-5" />, label: 'Instagram', placeholder: 'Instagram 프로필 URL' },
    x: { icon: <FaXTwitter className="w-5 h-5" />, label: 'X (Twitter)', placeholder: 'X 프로필 URL' },
    youtube: { icon: <FaYoutube className="w-5 h-5" />, label: 'YouTube', placeholder: 'YouTube 채널 URL' },
    discord: { icon: <FaDiscord className="w-5 h-5" />, label: 'Discord', placeholder: 'Discord 초대 링크' },
    facebook: { icon: <FaFacebook className="w-5 h-5" />, label: 'Facebook', placeholder: 'Facebook 프로필 URL' }, // 확장성을 위해 추가
    tiktok: { icon: <FaTiktok className="w-5 h-5" />, label: 'TikTok', placeholder: 'TikTok 프로필 URL' },   // 확장성을 위해 추가
};

// --- 이미지 자르기 유틸리티 ---
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new window.Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

async function getCroppedImg(imageSrc, pixelCrop) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
            }
            blob.name = 'cropped.png';
            resolve(blob);
        }, 'image/png');
    });
}

// --- 독립 컴포넌트들 ---

const SectionCard = ({ title, icon, children, className = "" }) => (
    <section className={`bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 shadow-lg overflow-hidden ${className}`}>
        <div className="flex items-center p-5 bg-black/10">
            {icon}
            <h2 className="text-xl font-semibold text-white ml-3">{title}</h2>
        </div>
        <div className="p-6 space-y-5">
            {children}
        </div>
    </section>
);

const ImageUploader = ({ label, imageUrl, onImageClick, onFileDrop, isProfile = false, hint }) => {
    const [isDragging, setIsDragging] = useState(false);
    const shape = isProfile ? 'rounded-full' : 'rounded-xl';
    return (
        <div className="flex flex-col items-center">
            <span className="block text-sm font-medium text-gray-300 mb-3">{label}</span>
            <button
                type="button"
                onClick={onImageClick}
                onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    const file = event.dataTransfer.files[0];
                    if (file?.type.startsWith('image/')) onFileDrop(file);
                }}
                className={`relative cursor-pointer group w-32 h-32 md:w-36 md:h-36 ${shape} ${isDragging ? 'ring-2 ring-indigo-400 ring-offset-2 ring-offset-[#111]' : ''}`}
            >
                <img
                    src={imageUrl || 'https://placehold.co/144x144/1F2937/9CA3AF?text=...'}
                    alt={`${label} 미리보기`}
                    className={`w-full h-full object-cover bg-gray-800 ${shape}`}
                />
                <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity ${shape} ${isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {isDragging ? <span className="text-sm font-semibold text-white">여기에 놓기</span> : <Pencil className="text-white w-8 h-8" />}
                </div>
            </button>
            <span className="mt-2 text-center text-xs leading-5 text-gray-500">{hint}<br />클릭하거나 이미지를 끌어다 놓으세요.</span>
        </div>
    );
};

const OptionButton = ({ onClick, isSelected, children, className = '' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex-1 p-3 text-center border rounded-lg transition-all duration-200
      ${isSelected
                ? 'bg-indigo-600 border-indigo-500 text-white font-semibold shadow-lg shadow-indigo-600/30'
                : 'bg-white/10 border-white/10 text-gray-300 hover:bg-white/20'
            } ${className}`}
    >
        {children}
    </button>
);

const TabButton = ({ label, icon, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`flex-1 flex flex-col items-center justify-center gap-1 p-3 border-b-2 font-medium transition-all
      ${isActive
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
    >
        {icon}
        <span className="text-sm">{label}</span>
    </button>
);

const LayoutButton = ({ onClick, isSelected, icon }) => (
    <OptionButton
        onClick={onClick}
        isSelected={isSelected}
        className="aspect-[3/4] !flex-none w-24 h-32"
    >
        <div className="flex flex-col items-center justify-center h-full">
            {icon}
        </div>
    </OptionButton>
);

const ALIGNMENTS = [
    { value: 'left', label: '왼쪽 정렬', icon: AlignLeft },
    { value: 'center', label: '가운데 정렬', icon: AlignCenter },
    { value: 'right', label: '오른쪽 정렬', icon: AlignRight },
];

const ColorField = ({ label, value, onChange }) => (
    <label className="flex min-w-0 flex-1 items-center justify-between gap-3 text-xs text-gray-400">
        <span>{label}</span>
        <span className="relative h-8 w-10 shrink-0 overflow-hidden rounded-md border border-white/15 bg-white/5">
            <input
                type="color"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="absolute -inset-2 h-12 w-14 cursor-pointer border-0 bg-transparent p-0"
            />
        </span>
    </label>
);

const AlignmentControl = ({ value = 'center', onChange }) => (
    <div className="flex items-center gap-1 rounded-lg bg-black/20 p-1" aria-label="글자 정렬">
        {ALIGNMENTS.map(({ value: alignment, label, icon: Icon }) => (
            <button
                key={alignment}
                type="button"
                title={label}
                aria-label={label}
                onClick={() => onChange(alignment)}
                className={`flex h-8 w-9 items-center justify-center rounded-md transition-colors ${value === alignment
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
            >
                <Icon className="h-4 w-4" />
            </button>
        ))}
    </div>
);

const BlockStyleControls = ({ block, onUpdate }) => {
    const isLink = block.type === 'link';
    const isText = block.type === 'text';
    const hasTextColor = isLink || isText || block.type === 'music' || block.type === 'ai';
    if (!hasTextColor) return null;
    const defaultTextColor = block.type === 'ai' && block.variant === 'discord'
        ? '#f2f3f5'
        : (isLink ? '#ffffff' : '#1f2937');

    return (
        <div className="space-y-3 border-t border-white/5 pt-3">
            {(isLink || isText) && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs font-medium text-gray-300">글자 정렬</span>
                    <AlignmentControl
                        value={block.align || 'center'}
                        onChange={(value) => onUpdate(block.id, 'align', value)}
                    />
                </div>
            )}
            <div className="flex gap-5">
                {isLink && (
                    <ColorField
                        label="버튼 색"
                        value={block.buttonColor || '#6366f1'}
                        onChange={(value) => onUpdate(block.id, 'buttonColor', value)}
                    />
                )}
                <ColorField
                    label="글자 색"
                    value={block.textColor || defaultTextColor}
                    onChange={(value) => onUpdate(block.id, 'textColor', value)}
                />
            </div>
            {isLink && (
                <div>
                    <span className="mb-2 block text-xs font-medium text-gray-300">버튼 모양</span>
                    <div className="grid grid-cols-3 gap-1 rounded-lg bg-black/20 p-1">
                        {[
                            ['sharp', '각진'],
                            ['rounded', '둥근'],
                            ['pill', '알약'],
                        ].map(([shape, label]) => (
                            <button
                                key={shape}
                                type="button"
                                onClick={() => onUpdate(block.id, 'shape', shape)}
                                className={`h-8 rounded-md text-xs transition-colors ${(block.shape || 'rounded') === shape
                                    ? 'bg-indigo-600 text-white'
                                    : 'text-gray-400 hover:bg-white/10 hover:text-white'
                                    }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- 드래그 가능한 블록 아이템 ---
const DraggableBlockItem = ({ block, onUpdate, onDelete }) => {
    const dragControls = useDragControls();

    // [신규] SNS 아이템 추가 핸들러
    const handleAddSnsItem = (platform) => {
        const currentItems = block.items || [];
        const newItem = {
            id: generateId(),
            platform: platform,
            url: ''
        };
        onUpdate(block.id, 'items', [...currentItems, newItem]);
    };

    // [신규] SNS 아이템 삭제 핸들러
    const handleRemoveSnsItem = (itemId) => {
        const currentItems = block.items || [];
        onUpdate(block.id, 'items', currentItems.filter(item => item.id !== itemId));
    };

    // [신규] SNS 아이템 URL 변경 핸들러
    const handleSnsItemChange = (itemId, value) => {
        const currentItems = block.items || [];
        const newItems = currentItems.map(item => 
            item.id === itemId ? { ...item, url: value } : item
        );
        onUpdate(block.id, 'items', newItems);
    };

    return (
        <Reorder.Item
            value={block}
            id={block.id}
            dragListener={false}
            dragControls={dragControls}
            className="bg-white/5 border border-white/10 rounded-xl p-4 mb-3 relative group touch-none"
        >
            <div className="flex items-start gap-3">
                <div
                    className="mt-2 cursor-grab active:cursor-grabbing text-gray-500 hover:text-white transition-colors p-1"
                    onPointerDown={(e) => dragControls.start(e)}
                >
                    <GripVertical className="w-6 h-6" />
                </div>

                <div className="flex-grow space-y-3 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                            {getBlockIcon(block.type)}
                            <span className="text-sm font-medium text-indigo-400 uppercase">{getBlockLabel(block.type)}</span>
                        </div>
                        <button
                            onClick={() => onDelete(block.id)}
                            className="text-gray-500 hover:text-red-400 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>

                    {/* --- 링크 블록 --- */}
                    {block.type === 'link' && (
                        <>
                            <input
                                type="text"
                                placeholder="버튼 이름"
                                value={block.title || ''}
                                onChange={(e) => onUpdate(block.id, 'title', e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded p-2 text-sm text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none"
                            />
                            <input
                                type="text"
                                placeholder="URL (https://...)"
                                value={block.url || ''}
                                onChange={(e) => onUpdate(block.id, 'url', e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded p-2 text-sm text-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none"
                            />
                        </>
                    )}
                    {/* --- 텍스트 블록 --- */}
                    {block.type === 'text' && (
                        <textarea
                            placeholder="텍스트를 입력하세요"
                            value={block.content || ''}
                            onChange={(e) => onUpdate(block.id, 'content', e.target.value)}
                            rows={2}
                            className="w-full bg-black/20 border border-white/10 rounded p-2 text-sm text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none"
                        />
                    )}

                    <BlockStyleControls block={block} onUpdate={onUpdate} />

                    {block.type === 'music' && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/15 px-3 py-2 text-xs text-gray-400">
                                <FaYoutube className="h-4 w-4 text-red-400" />
                                <FaSpotify className="h-4 w-4 text-emerald-400" />
                                YouTube 또는 Spotify 음악 주소를 사용할 수 있습니다.
                            </div>
                            <input
                                type="text"
                                placeholder="음악 URL"
                                value={block.url || ''}
                                onChange={(e) => onUpdate(block.id, 'url', e.target.value)}
                                className="w-full rounded border border-white/10 bg-black/20 p-2 text-sm text-white outline-none transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                            <input
                                type="text"
                                placeholder="표시할 문구 (선택)"
                                maxLength={100}
                                value={block.title || ''}
                                onChange={(e) => onUpdate(block.id, 'title', e.target.value)}
                                className="w-full rounded border border-white/10 bg-black/20 p-2 text-sm text-white outline-none transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                    )}

                    {block.type === 'ai' && (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2 text-xs leading-5 text-indigo-100">
                                방문자가 질문하면 입력한 말투와 정보만 참고해 답변합니다. 비밀번호, 주소, 연락처처럼 공개하면 안 되는 정보는 입력하지 마세요.
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-gray-300">블록 이름</label>
                                <input
                                    type="text"
                                    placeholder="AI에게 물어보기"
                                    maxLength={60}
                                    value={block.title || ''}
                                    onChange={(e) => onUpdate(block.id, 'title', e.target.value)}
                                    className="w-full rounded border border-white/10 bg-black/20 p-2 text-sm text-white outline-none focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-xs font-medium text-gray-300">답변 형식</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        ['default', '기본', '깔끔한 입력창'],
                                        ['discord', 'Discord DM', '채팅 메시지'],
                                        ['bubble', '말풍선', '가벼운 대화'],
                                    ].map(([variant, label, description]) => (
                                        <button
                                            key={variant}
                                            type="button"
                                            onClick={() => onUpdate(block.id, 'variant', variant)}
                                            className={`min-h-16 rounded-lg border px-2 py-2 text-left transition-colors ${(block.variant || 'default') === variant
                                                ? 'border-indigo-500 bg-indigo-500/15 text-white'
                                                : 'border-white/10 bg-black/15 text-gray-400 hover:border-white/20 hover:text-white'
                                                }`}
                                        >
                                            <span className="block text-xs font-semibold">{label}</span>
                                            <span className="mt-1 block text-[10px] leading-4 opacity-70">{description}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="mb-1.5 flex items-center justify-between text-xs">
                                    <label className="font-medium text-gray-300">평소 말투</label>
                                    <span className="text-gray-500">{(block.tone || '').length}/300</span>
                                </div>
                                <textarea
                                    placeholder="예: 반말을 쓰고 문장 끝에 'ㅋㅋ'를 자주 붙여. 답변은 짧고 솔직하게 해."
                                    maxLength={300}
                                    rows={4}
                                    value={block.tone || ''}
                                    onChange={(e) => onUpdate(block.id, 'tone', e.target.value)}
                                    className="w-full resize-y rounded border border-white/10 bg-black/20 p-2 text-sm text-white outline-none focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <div className="mb-1.5 flex items-center justify-between text-xs">
                                    <label className="font-medium text-gray-300">나에 대한 정보</label>
                                    <span className="text-gray-500">{(block.knowledge || '').length.toLocaleString()}/20,000</span>
                                </div>
                                <textarea
                                    placeholder="예: 좋아하는 게임, 취미, 활동 시간, 자주 받는 질문의 답변 등을 적어주세요."
                                    maxLength={20000}
                                    rows={7}
                                    value={block.knowledge || ''}
                                    onChange={(e) => onUpdate(block.id, 'knowledge', e.target.value)}
                                    className="w-full resize-y rounded border border-white/10 bg-black/20 p-2 text-sm text-white outline-none focus:border-indigo-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* --- [수정] SNS 연결 블록 UI (동적 리스트 방식) --- */}
                    {block.type === 'sns' && (
                        <div className="space-y-3">
                            {/* 추가된 SNS 리스트 렌더링 */}
                            {(block.items || []).map((item) => (
                                <div key={item.id} className="flex items-center gap-3 animate-fadeIn">
                                    <div className="shrink-0 text-gray-400">
                                        {SNS_PLATFORMS[item.platform]?.icon}
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={SNS_PLATFORMS[item.platform]?.placeholder}
                                        value={item.url || ''}
                                        onChange={(e) => handleSnsItemChange(item.id, e.target.value)}
                                        className="w-full flex-1 min-w-0 bg-black/20 border border-white/10 rounded p-2 text-sm text-white focus:border-indigo-500 outline-none"
                                    />
                                    <button 
                                        onClick={() => handleRemoveSnsItem(item.id)}
                                        className="text-gray-500 hover:text-red-400 p-1"
                                    >
                                        <LucideX className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}

                            {/* SNS 추가 버튼 영역 */}
                            <div className="pt-2 border-t border-white/5">
                                <p className="text-xs text-gray-500 mb-2">SNS 선택:</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(SNS_PLATFORMS).map(([key, platform]) => (
                                        <button
                                            key={key}
                                            onClick={() => handleAddSnsItem(key)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 rounded-lg text-xs text-gray-300 hover:text-white transition-all"
                                        >
                                            {platform.icon}
                                            {platform.label}
                                            <Plus className="w-3 h-3 ml-1 opacity-50" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 크기 조절 */}
                    {['link', 'text'].includes(block.type) && (
                        <div className="pt-2 border-t border-white/5">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">글자 크기:</span>
                                <div className="flex gap-1 bg-black/20 rounded-lg p-1">
                                    {['small', 'medium', 'large'].map((size) => (
                                        <button
                                            key={size}
                                            onClick={() => onUpdate(block.id, 'size', size)}
                                            className={`px-3 py-1 text-xs rounded transition-colors ${block.size === size
                                                ? 'bg-indigo-600 text-white'
                                                : 'text-gray-400 hover:text-white'
                                                }`}
                                        >
                                            {{ small: 'S', medium: 'M', large: 'L' }[size]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Reorder.Item>
    );
};

// --- BlockList 컴포넌트 ---
const BlockList = ({ blocks, onReorder, onUpdate, onDelete }) => {
    return (
        <Reorder.Group axis="y" values={blocks} onReorder={onReorder} className="space-y-3">
            {blocks.map((block) => (
                <DraggableBlockItem
                    key={block.id}
                    block={block}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                />
            ))}
        </Reorder.Group>
    );
};

// 헬퍼
const getBlockIcon = (type) => {
    switch (type) {
        case 'link': return <LinkIcon className="w-4 h-4" />;
        case 'text': return <Type className="w-4 h-4" />;
        case 'image': return <ImageIcon className="w-4 h-4" />;
        case 'video': return <FaYoutube className="w-4 h-4" />;
        case 'map': return <MapPin className="w-4 h-4" />;
        case 'file': return <FileText className="w-4 h-4" />;
        case 'sns': return <FaInstagram className="w-4 h-4" />;
        case 'music': return <Music2 className="w-4 h-4" />;
        case 'ai': return <Sparkles className="w-4 h-4" />;
        default: return <LinkIcon className="w-4 h-4" />;
    }
};

const getBlockLabel = (type) => {
    switch (type) {
        case 'link': return '링크';
        case 'text': return '텍스트';
        case 'image': return '갤러리';
        case 'video': return '동영상';
        case 'map': return '지도';
        case 'file': return '파일';
        case 'sns': return 'SNS';
        case 'music': return '음악';
        case 'ai': return 'AI 답변';
        default: return '블록';
    }
};

const layoutIconMocks = {
    style1: (
        <div className="w-full h-full bg-white rounded flex flex-col items-center p-3 space-y-3">
            <div className="w-10 h-10 rounded-full bg-gray-300"></div>
            <div className="w-14 h-2.5 bg-gray-300 rounded-sm"></div>
            <div className="w-10 h-2.5 bg-gray-300 rounded-sm"></div>
        </div>
    ),
    style2: (
        <div className="w-full h-full bg-gray-700 rounded flex flex-col items-center p-3 space-y-3">
            <div className="w-10 h-10 rounded-full bg-gray-400"></div>
            <div className="w-14 h-2.5 bg-gray-400 rounded-sm"></div>
            <div className="w-10 h-2.5 bg-gray-400 rounded-sm"></div>
        </div>
    ),
    style4: (
        <div className="w-full h-full bg-white rounded flex flex-col items-center">
            <div className="w-full h-1/2 bg-gray-300 rounded-t flex items-center justify-center">
                <ImageIcon size={24} className="text-gray-500" />
            </div>
            <div className="w-full h-1/2 flex flex-col items-center p-2 space-y-2">
                <div className="w-14 h-2.5 bg-gray-400 rounded-sm"></div>
                <div className="w-10 h-2.5 bg-gray-400 rounded-sm"></div>
            </div>
        </div>
    ),
};


// --- [메인 컴포넌트] ---
function ProfileDesignPage() {
    const location = useLocation();
    const locale = getLocaleFromPathname(location.pathname);
    const isVi = locale === 'vi';
    const tx = useCallback((ko, vi) => (isVi ? vi : ko), [isVi]);

    const [profile, setProfile] = useState({
        unique_id: '',
        profile_image_url: null,
        background_image_url: null,
        layout_style: 'style1',
        display_name: '',
        description: '',
        display_name_color: '#111827',
        description_color: '#4B5563',
        font_size: 'medium',
        background_color: '#FFFFFF',
        button_color: '#000000ff',
        font_family: 'default',
        button_shape: 'rounded',
        blocks: [],
    });

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState({ type: 'idle', message: '' });
    const [activeTab, setActiveTab] = useState('page');

    const [cropperOpen, setCropperOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [imageTypeToCrop, setImageTypeToCrop] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropShape, setCropShape] = useState('rect');
    const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [history, setHistory] = useState({ today_visitors: 0, ai_history: [], total: 0, page_size: 20 });
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historyPage, setHistoryPage] = useState(1);
    const [historySearch, setHistorySearch] = useState('');
    const [historyQuery, setHistoryQuery] = useState('');
    const [historyRefresh, setHistoryRefresh] = useState(0);
    const [profileURLCopied, setProfileURLCopied] = useState(false);
    const [profileURLAvailable, setProfileURLAvailable] = useState(false);

    const profileInputRef = useRef(null);
    const backgroundInputRef = useRef(null);

    const debouncedProfile = useDebounce(profile, 1500);
    const isInitialMount = useRef(true);

    // 1. 프로필 불러오기 (SNS 데이터 파싱 로직 수정)
    useEffect(() => {
        const fetchProfile = async () => {
            setIsLoading(true);
            try {
                const response = await fetch('/api/design/');
                if (response.status === 401) {
                    setError('LOGIN_REQUIRED');
                    setIsLoading(false);
                    return;
                }
                if (response.ok) {
                    const data = await response.json();
                    let parsedBlocks = [];
                    let rawLinks = [];

                    try {
                        if (data.links && data.links.Valid && data.links.String) {
                            rawLinks = JSON.parse(data.links.String);
                        } else if (typeof data.links === 'string') {
                            rawLinks = JSON.parse(data.links);
                        } else if (Array.isArray(data.links)) {
                            rawLinks = data.links;
                        }
                    } catch (e) { console.error(e); }

                    parsedBlocks = rawLinks.map((link, index) => {
                        const url = link.url || '';
                        if (url.startsWith('internal://type=text')) {
                            const sizeMatch = url.match(/size=([^&]+)/);
                            return {
                                id: `rec-${index}-${generateId()}`,
                                type: 'text',
                                content: link.title,
                                size: link.style?.size || (sizeMatch ? sizeMatch[1] : 'medium'),
                                textColor: link.style?.textColor || '#1f2937',
                                align: link.style?.align || 'center'
                            };
                        }
                        if (url.startsWith('internal://type=image')) {
                            return { id: `rec-${index}-${generateId()}`, type: 'image', content: link.title };
                        }
                        if (url.startsWith('internal://type=music')) {
                            try {
                                const music = JSON.parse(link.title || '{}');
                                return { id: `rec-${index}-${generateId()}`, type: 'music', title: music.title || '', url: music.url || '', textColor: music.textColor || '' };
                            } catch (e) {
                                return { id: `rec-${index}-${generateId()}`, type: 'music', title: '', url: '' };
                            }
                        }
                        if (url.startsWith('internal://type=ai')) {
                            const variantMatch = url.match(/[?&]variant=(default|discord|bubble)/);
                            return {
                                id: `rec-${index}-${generateId()}`,
                                type: 'ai',
                                title: link.title || 'AI에게 물어보기',
                                tone: data.ai_tone || '',
                                knowledge: data.ai_knowledge || '',
                                variant: variantMatch?.[1] || link.style?.variant || 'default',
                                textColor: link.style?.textColor || ''
                            };
                        }
                        if (url.includes('#type=video')) {
                            return { id: `rec-${index}-${generateId()}`, type: 'video', title: link.title, url: url.replace('#type=video', '') };
                        }
                        // [수정] SNS 블록 불러오기 로직
                        if (url.startsWith('internal://type=sns')) {
                            let snsItems = [];
                            try {
                                const parsedData = JSON.parse(link.title || '{}');
                                // 배열 형태(신규)인지 객체 형태(구형)인지 확인
                                if (Array.isArray(parsedData)) {
                                    snsItems = parsedData;
                                } else {
                                    // 구형 데이터(객체)를 배열로 변환하여 호환성 유지
                                    if (parsedData.instagram_url) snsItems.push({ id: generateId(), platform: 'instagram', url: parsedData.instagram_url });
                                    if (parsedData.x_url) snsItems.push({ id: generateId(), platform: 'x', url: parsedData.x_url });
                                    if (parsedData.youtube_url) snsItems.push({ id: generateId(), platform: 'youtube', url: parsedData.youtube_url });
                                    if (parsedData.discord_url) snsItems.push({ id: generateId(), platform: 'discord', url: parsedData.discord_url });
                                }
                            } catch (e) { snsItems = []; }

                            return {
                                id: `rec-${index}-${generateId()}`,
                                type: 'sns',
                                items: snsItems
                            };
                        }
                        return {
                            id: `rec-${index}-${generateId()}`,
                            type: 'link',
                            title: link.title,
                            url: link.url,
                            size: link.style?.size || 'medium',
                            buttonColor: link.style?.buttonColor || data.button_color || '#6366f1',
                            textColor: link.style?.textColor || '#ffffff',
                            shape: link.style?.shape || data.button_shape || 'rounded',
                            align: link.style?.align || 'center'
                        };
                    });

                    setProfile({
                        unique_id: data.unique_id,
                        profile_image_url: data.profile_image_url?.Valid ? data.profile_image_url.String : null,
                        background_image_url: data.background_image_url?.Valid ? data.background_image_url.String : null,
                        layout_style: data.layout_style || 'style1',
                        display_name: data.display_name?.Valid ? data.display_name.String : '',
                        description: data.description?.Valid ? data.description.String : '',
                        display_name_color: data.display_name_color || '#111827',
                        description_color: data.description_color || '#4B5563',
                        font_size: data.font_size || 'medium',
                        background_color: data.background_color || '#FFFFFF',
                        button_color: data.button_color || '#6366F1',
                        font_family: data.font_family || 'default',
                        button_shape: data.button_shape || 'rounded',
                        blocks: parsedBlocks,
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
                setTimeout(() => { isInitialMount.current = false; }, 1500);
            }
        };
        fetchProfile();
    }, []);

    // 2. 자동 저장 (SNS 블록 저장 로직 수정)
    const autoSaveTextData = useCallback(async (profileToSave) => {
        if (isInitialMount.current) return;
        setSaveStatus({ type: 'saving', message: tx('저장 중...', 'Đang lưu...') });
        try {
            const convertedLinks = profileToSave.blocks.map(block => {
                if (block.type === 'text') {
                    return {
                        title: block.content,
                        url: 'internal://type=text&size=' + (block.size || 'medium'),
                        style: {
                            size: block.size || 'medium',
                            textColor: block.textColor || '#1f2937',
                            align: block.align || 'center'
                        }
                    };
                }
                if (block.type === 'image') {
                    return { title: block.content, url: 'internal://type=image' };
                }
                if (block.type === 'video') {
                    return { title: block.title, url: block.url ? block.url + '#type=video' : '' };
                }
                if (block.type === 'music') {
                    return {
                        title: JSON.stringify({ title: block.title || '', url: block.url || '', textColor: block.textColor || '' }),
                        url: 'internal://type=music'
                    };
                }
                if (block.type === 'ai') {
                    const variant = ['default', 'discord', 'bubble'].includes(block.variant) ? block.variant : 'default';
                    return {
                        title: block.title || 'AI에게 물어보기',
                        url: `internal://type=ai&variant=${variant}`,
                        style: { variant, textColor: block.textColor || '' }
                    };
                }
                // [수정] SNS 블록 저장: items 배열 전체를 JSON 문자열로 저장
                if (block.type === 'sns') {
                    return {
                        title: JSON.stringify(block.items || []),
                        url: 'internal://type=sns'
                    };
                }
                // 기본 링크 블록
                return {
                    title: block.title,
                    url: block.url,
                    style: {
                        size: block.size || 'medium',
                        buttonColor: block.buttonColor || '#6366f1',
                        textColor: block.textColor || '#ffffff',
                        shape: block.shape || 'rounded',
                        align: block.align || 'center'
                    }
                };
            });

            const aiBlock = profileToSave.blocks.find((block) => block.type === 'ai');
            const payload = {
                ...profileToSave,
                links: convertedLinks,
                ai_enabled: Boolean(aiBlock),
                ai_tone: aiBlock?.tone || '',
                ai_knowledge: aiBlock?.knowledge || ''
            };

            const response = await fetch('/api/design/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setSaveStatus({ type: 'success', message: profileToSave.unique_id });
                return true;
            } else {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Save failed');
            }
        } catch (err) {
            setSaveStatus({ type: 'error', message: tx('저장 실패', 'Lưu thất bại') });
            return false;
        }
    }, [tx]);

    useEffect(() => {
        if (!isInitialMount.current) {
            autoSaveTextData(debouncedProfile);
        }
    }, [debouncedProfile, autoSaveTextData]);

    useEffect(() => {
        if (activeTab !== 'history') return;
        const controller = new AbortController();
        setHistoryLoading(true);
        setHistoryError('');
        const params = new URLSearchParams({ page: String(historyPage) });
        if (historyQuery) params.set('q', historyQuery);
        fetch(`/api/design/history?${params}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error('기록을 불러오지 못했습니다.');
                return response.json();
            })
            .then((data) => setHistory({
                today_visitors: data.today_visitors || 0,
                ai_history: data.ai_history || [],
                total: data.total || 0,
                page_size: data.page_size || 20
            }))
            .catch((fetchError) => {
                if (fetchError.name !== 'AbortError') setHistoryError(fetchError.message);
            })
            .finally(() => setHistoryLoading(false));
        return () => controller.abort();
    }, [activeTab, historyPage, historyQuery, historyRefresh]);

    useEffect(() => {
        const id = profile.unique_id.trim();
        setProfileURLAvailable(false);
        if (!/^[a-zA-Z0-9_.-]{3,50}$/.test(id)) return;
        const controller = new AbortController();
        const timer = setTimeout(() => {
            fetch('/api/design/check-id', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ unique_id: id }),
                signal: controller.signal
            }).then((response) => setProfileURLAvailable(response.ok)).catch(() => setProfileURLAvailable(false));
        }, 400);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [profile.unique_id]);

    const deleteHistoryEntry = async (id) => {
        if (!window.confirm('이 AI 답변 기록을 삭제할까요?')) return;
        const response = await fetch(`/api/design/history/${id}`, { method: 'DELETE' });
        if (!response.ok) return setHistoryError('기록을 삭제하지 못했습니다.');
        if (history.ai_history.length === 1 && historyPage > 1) setHistoryPage(historyPage - 1);
        else setHistoryRefresh((value) => value + 1);
    };

    const clearHistory = async () => {
        if (!window.confirm('AI 질문과 답변 기록을 모두 삭제할까요? 이 작업은 되돌릴 수 없습니다.')) return;
        const response = await fetch('/api/design/history', { method: 'DELETE' });
        if (!response.ok) return setHistoryError('기록을 초기화하지 못했습니다.');
        setHistoryPage(1);
        setHistoryRefresh((value) => value + 1);
    };

    const copyProfileURL = async () => {
        if (!profileURLAvailable) return;
        const id = profile.unique_id.trim();
        await navigator.clipboard.writeText(`https://disparty.com/${id}`);
        setProfileURLCopied(true);
        setTimeout(() => setProfileURLCopied(false), 1500);
    };

    // 핸들러들
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile((prev) => ({ ...prev, [name]: value }));
    };
    const handleOptionChange = (name, value) => {
        setProfile((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddBlock = (type) => {
        const newBlock = {
            id: generateId(),
            type,
            title: '',
            url: '',
            content: '',
            size: 'medium',
            align: 'center',
        };
        if (type === 'link') {
            newBlock.buttonColor = '#6366f1';
            newBlock.textColor = '#ffffff';
            newBlock.shape = 'rounded';
        }
        if (type === 'text') {
            newBlock.textColor = '#1f2937';
        }
        // [수정] SNS 블록일 경우 빈 배열로 초기화 (유저가 선택해서 추가하도록)
        if (type === 'sns') {
            newBlock.items = [];
        }
        if (type === 'music') {
            newBlock.title = '';
            newBlock.url = '';
        }
        if (type === 'ai') {
            if (profile.blocks.some((block) => block.type === 'ai')) {
                setIsBlockModalOpen(false);
                return;
            }
            newBlock.title = 'AI에게 물어보기';
            newBlock.tone = '';
            newBlock.knowledge = '';
            newBlock.variant = 'default';
        }
        setProfile(prev => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
        setIsBlockModalOpen(false);
    };

    const handleUpdateBlock = useCallback((id, field, value) => {
        setProfile(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => b.id === id ? { ...b, [field]: value } : b)
        }));
    }, []);

    const handleRemoveBlock = useCallback((id) => {
        if (window.confirm(tx('이 블록을 삭제하시겠습니까?', 'Bạn có chắc muốn xóa block này?'))) {
            setProfile(prev => ({
                ...prev,
                blocks: prev.blocks.filter(b => b.id !== id)
            }));
        }
    }, [tx]);

    const handleReorderBlocks = (newOrder) => {
        setProfile(prev => ({ ...prev, blocks: newOrder }));
    };

    // 이미지 관련 핸들러
    const openImageCrop = (file, imageTypeField) => {
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result);
                setImageTypeToCrop(imageTypeField);
                setCropShape(imageTypeField === 'profile_image_url' ? 'round' : 'rect');
                setCropperOpen(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageFileChange = (e, imageTypeField) => {
        openImageCrop(e.target.files[0], imageTypeField);
        e.target.value = null;
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropApply = async () => {
        if (!croppedAreaPixels || !imageToCrop || !imageTypeToCrop) return;
        setSaveStatus({ type: 'saving', message: tx('이미지 업로드 중...', 'Đang tải hình ảnh...') });
        setCropperOpen(false);
        try {
            const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
            const formData = new FormData();
            formData.append('image', croppedImageBlob, 'cropped.png');
            const uploadUrl = imageTypeToCrop === 'profile_image_url' ? '/api/design/upload/profile' : '/api/design/upload/background';
            let response = await fetch(uploadUrl, { method: 'POST', body: formData });
            if (response.status === 404 && await autoSaveTextData(profile)) {
                response = await fetch(uploadUrl, { method: 'POST', body: formData });
            }
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.message || 'Image upload failed');
            setProfile((prev) => ({ ...prev, [imageTypeToCrop]: data.filename }));
            setSaveStatus({ type: 'success', message: profile.unique_id });
        } catch (err) {
            setSaveStatus({ type: 'error', message: err.message || tx('이미지 실패', 'Tải hình thất bại') });
        } finally {
            setImageToCrop(null);
        }
    };

    const handleDeleteImage = async (imageTypeField) => {
        if (!window.confirm(tx("삭제하시겠습니까?", "Bạn có chắc muốn xóa?"))) return;
        const oldImageUrl = profile[imageTypeField];
        setProfile((prev) => ({ ...prev, [imageTypeField]: null }));
        try {
            const deleteUrl = imageTypeField === 'profile_image_url' ? '/api/design/delete/profile' : '/api/design/delete/background';
            await fetch(deleteUrl, { method: 'POST' });
            setSaveStatus({ type: 'success', message: tx('삭제 완료', 'Đã xóa') });
        } catch (e) {
            setProfile((prev) => ({ ...prev, [imageTypeField]: oldImageUrl }));
        }
    };

    if (isLoading) return <div className="text-white text-center p-10">{tx("로딩 중...", "Đang tải...")}</div>;

    if (error === 'LOGIN_REQUIRED') {
        return (
            <div className="min-h-screen bg-[#111] flex flex-col items-center justify-center p-4 text-center">
                <div className="bg-white/5 p-8 rounded-2xl border border-white/10 flex flex-col items-center max-w-sm w-full shadow-xl backdrop-blur-sm">
                    <div className="bg-indigo-500/20 p-4 rounded-full mb-4">
                        <User className="w-8 h-8 text-indigo-500" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">{tx("로그인이 필요합니다", "Cần đăng nhập")}</h2>
                    <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                        {tx("프로필 디자인 페이지를 이용하려면", "Để sử dụng trang thiết kế hồ sơ")}<br />{tx("먼저 로그인을 해주세요.", "hãy đăng nhập trước.")}
                    </p>
                    <a
                        href="https://disparty.com/profile"
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
                    >
                        {tx("로그인 하러가기", "Đi đăng nhập")}
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
        );
    }

    const renderEditor = () => (
        <div className="w-full h-full text-white pb-24 mt-14 md:mt-0">
            <div className="sticky top-0 z-20 bg-[#111]/80 backdrop-blur-md border-b border-gray-700">
                <div className="flex max-w-lg mx-auto">
                    <TabButton label={tx("페이지", "Trang")} icon={<Brush className="w-5 h-5" />} isActive={activeTab === 'page'} onClick={() => setActiveTab('page')} />
                    <TabButton label={tx("디자인", "Thiết kế")} icon={<Palette className="w-5 h-5" />} isActive={activeTab === 'design'} onClick={() => setActiveTab('design')} />
                    <TabButton label="기록" icon={<HistoryIcon className="w-5 h-5" />} isActive={activeTab === 'history'} onClick={() => setActiveTab('history')} />
                </div>
            </div>

            <div className="container mx-auto max-w-lg p-4">
                <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFileChange(e, 'profile_image_url')} />
                <input ref={backgroundInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFileChange(e, 'background_image_url')} />

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, x: activeTab === 'page' ? -20 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-6"
                    >
                        {activeTab === 'page' ? (
                            <>
                                <SectionCard title={tx("고유 ID (실제 URL로 사용됩니다.)", "ID duy nhất (được dùng làm URL)")} icon={<User className="text-indigo-400" />}>
                                    <input
                                        type="text" name="unique_id" value={profile.unique_id}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                                        placeholder={tx("ID 입력", "Nhập ID")}
                                    />
                                    {profileURLAvailable && (
                                        <button type="button" onClick={copyProfileURL} className="mt-3 flex max-w-full items-center gap-1.5 text-left text-sm text-indigo-400 hover:text-indigo-300">
                                            <Copy className="h-4 w-4 flex-none" />
                                            <span className="truncate">https://disparty.com/{profile.unique_id.trim()}</span>
                                            {profileURLCopied && <span className="flex-none text-xs text-green-400">복사됨</span>}
                                        </button>
                                    )}
                                </SectionCard>

                                <SectionCard title={tx("프로필 정보", "Thông tin hồ sơ")} icon={<Type className="text-indigo-400" />}>
                                    <input
                                        type="text" name="display_name" value={profile.display_name}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white mb-3"
                                        placeholder={tx("제목", "Tiêu đề")}
                                    />
                                    <textarea
                                        name="description" value={profile.description}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                                        placeholder={tx("소개글", "Giới thiệu")}
                                        rows={3}
                                    />
                                    <div className="flex gap-5 border-t border-white/10 pt-4">
                                        <ColorField label="이름 색" value={profile.display_name_color || '#111827'} onChange={(value) => handleOptionChange('display_name_color', value)} />
                                        <ColorField label="소개글 색" value={profile.description_color || '#4B5563'} onChange={(value) => handleOptionChange('description_color', value)} />
                                    </div>
                                </SectionCard>

                                <SectionCard title={tx("블록 관리", "Quản lý block")} icon={<LayoutList className="text-indigo-400" />}>
                                    <p className="text-sm text-gray-400 mb-4">
                                        {tx("왼쪽 점 6개를 드래그하여 순서를 변경할 수 있습니다.", "Kéo 6 chấm bên trái để đổi thứ tự.")}
                                    </p>

                                    <BlockList
                                        blocks={profile.blocks}
                                        onReorder={handleReorderBlocks}
                                        onUpdate={handleUpdateBlock}
                                        onDelete={handleRemoveBlock}
                                    />

                                    <button
                                        onClick={() => setIsBlockModalOpen(true)}
                                        className="w-full py-4 mt-2 border-2 border-dashed border-indigo-500/50 rounded-xl text-indigo-300 hover:bg-indigo-500/10 hover:border-indigo-500 transition-all flex items-center justify-center gap-2 font-medium"
                                    >
                                        <PlusCircle className="w-5 h-5" />
                                        {tx("+ 블록 추가", "+ Thêm block")}
                                    </button>
                                </SectionCard>
                            </>
                        ) : activeTab === 'design' ? (
                            <>
                                <SectionCard title={tx("레이아웃", "Bố cục")} icon={<LayoutList className="text-indigo-400" />}>
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {['style1', 'style2', 'style4'].map((layout) => (
                                            <LayoutButton key={layout} onClick={() => handleOptionChange('layout_style', layout)} isSelected={profile.layout_style === layout} icon={layoutIconMocks[layout]} />
                                        ))}
                                    </div>
                                </SectionCard>
                                <SectionCard title={tx("이미지", "Hình ảnh")} icon={<ImageIcon className="text-indigo-400" />}>
                                    <div className="flex justify-center gap-4">
                                        <ImageUploader label={tx("프로필", "Hồ sơ")} imageUrl={getImageUrl(profile.profile_image_url)} onImageClick={() => profileInputRef.current.click()} onFileDrop={(file) => openImageCrop(file, 'profile_image_url')} isProfile={true} hint={tx("권장 800×800px (1:1)", "Khuyến nghị 800×800px (1:1)")} />
                                        {profile.layout_style === 'style4' && (
                                            <ImageUploader label={tx("배경", "Nền")} imageUrl={getImageUrl(profile.background_image_url)} onImageClick={() => backgroundInputRef.current.click()} onFileDrop={(file) => openImageCrop(file, 'background_image_url')} hint={tx("권장 1600×900px (16:9)", "Khuyến nghị 1600×900px (16:9)")} />
                                        )}
                                    </div>

                                    {profile.profile_image_url && (
                                        <button onClick={() => handleDeleteImage('profile_image_url')} className="text-red-400 text-sm mt-2 w-full hover:bg-red-400/10 rounded py-1 transition-colors">
                                            {tx("프로필 이미지 삭제", "Xóa ảnh hồ sơ")}
                                        </button>
                                    )}

                                    {profile.layout_style === 'style4' && profile.background_image_url && (
                                        <button onClick={() => handleDeleteImage('background_image_url')} className="text-red-400 text-sm mt-1 w-full hover:bg-red-400/10 rounded py-1 transition-colors">
                                            {tx("배경 이미지 삭제", "Xóa ảnh nền")}
                                        </button>
                                    )}
                                </SectionCard>
                                <SectionCard title={tx("스타일", "Kiểu dáng")} icon={<Palette className="text-indigo-400" />}>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-sm text-gray-400">{tx("배경색", "Màu nền")}</label>
                                            <input type="color" name="background_color" value={profile.background_color} onChange={handleInputChange} className="w-full h-10 mt-1 rounded cursor-pointer" />
                                        </div>
                                    </div>
                                </SectionCard>
                            </>
                        ) : (
                            <>
                                <SectionCard title="오늘의 방문자" icon={<BarChart3 className="text-indigo-400" />}>
                                    <div className="flex items-end gap-2">
                                        <strong className="text-4xl text-white">{historyLoading ? '-' : history.today_visitors}</strong>
                                        <span className="pb-1 text-sm text-gray-400">명</span>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-500">방문자 수는 하루에 한 번 집계되며 본인 방문은 제외됩니다.</p>
                                </SectionCard>

                                <SectionCard title="AI 답변 기록" icon={<HistoryIcon className="text-indigo-400" />}>
                                    <div className="mb-4 flex items-center justify-between gap-3">
                                        <p className="text-xs text-gray-500">최근 6개월 · 총 {history.total}개</p>
                                        {history.total > 0 && (
                                            <button type="button" onClick={clearHistory} className="text-xs text-red-400 hover:text-red-300">기록 초기화</button>
                                        )}
                                    </div>
                                    <form
                                        className="mb-4 flex gap-2"
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                            setHistoryPage(1);
                                            setHistoryQuery(historySearch.trim());
                                        }}
                                    >
                                        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3">
                                            <Search className="h-4 w-4 flex-none text-gray-500" />
                                            <input
                                                value={historySearch}
                                                onChange={(event) => setHistorySearch(event.target.value.slice(0, 100))}
                                                placeholder="질문 또는 답변 검색"
                                                className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-gray-600"
                                            />
                                        </div>
                                        <button type="submit" className="rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-500">검색</button>
                                    </form>
                                    {historyLoading && <div className="py-8 text-center text-sm text-gray-400">불러오는 중...</div>}
                                    {historyError && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-300">{historyError}</div>}
                                    {!historyLoading && !historyError && history.ai_history.length === 0 && (
                                        <div className="py-8 text-center text-sm text-gray-500">아직 AI 답변 기록이 없습니다.</div>
                                    )}
                                    <div className="space-y-3">
                                        {history.ai_history.map((entry) => (
                                            <article key={entry.id} className="rounded-lg border border-white/10 bg-black/20 p-4">
                                                <div className="mb-2 flex items-start justify-between gap-3">
                                                    <p className="text-sm font-semibold text-indigo-300">Q. {entry.question}</p>
                                                    <div className="flex flex-none items-center gap-2">
                                                        <time className="text-[11px] text-gray-500">{new Date(entry.created_at).toLocaleString('ko-KR')}</time>
                                                        <button type="button" onClick={() => deleteHistoryEntry(entry.id)} aria-label="기록 삭제" className="text-gray-500 hover:text-red-400">
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="whitespace-pre-wrap text-sm leading-6 text-gray-300">A. {entry.answer}</p>
                                            </article>
                                        ))}
                                    </div>
                                    {history.total > history.page_size && (
                                        <div className="mt-4 flex items-center justify-center gap-3 border-t border-white/10 pt-4">
                                            <button type="button" disabled={historyPage === 1 || historyLoading} onClick={() => setHistoryPage((page) => page - 1)} className="rounded-lg p-2 text-gray-300 hover:bg-white/10 disabled:opacity-30" aria-label="이전 페이지">
                                                <ChevronLeft className="h-5 w-5" />
                                            </button>
                                            <span className="text-sm text-gray-400">{historyPage} / {Math.ceil(history.total / history.page_size)}</span>
                                            <button type="button" disabled={historyPage >= Math.ceil(history.total / history.page_size) || historyLoading} onClick={() => setHistoryPage((page) => page + 1)} className="rounded-lg p-2 text-gray-300 hover:bg-white/10 disabled:opacity-30" aria-label="다음 페이지">
                                                <ChevronRight className="h-5 w-5" />
                                            </button>
                                        </div>
                                    )}
                                </SectionCard>
                            </>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );

    const BlockSelectionModal = () => (
        <AnimatePresence>
            {isBlockModalOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[1050] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
                    onClick={() => setIsBlockModalOpen(false)}
                >
                    <motion.div
                        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="bg-white w-full max-w-2xl rounded-t-2xl md:rounded-2xl p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-gray-900">{tx("블록 추가", "Thêm block")}</h3>
                            <button onClick={() => setIsBlockModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                <LucideX className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-500 mb-3">{tx("콘텐츠", "Nội dung")}</h4>
                                <div className="grid grid-cols-4 gap-4 md:gap-6">
                                    <BlockOption icon={<LinkIcon />} label={tx("단일 링크", "Liên kết")} onClick={() => handleAddBlock('link')} color="bg-green-100 text-green-600" />
                                    <BlockOption icon={<Type />} label={tx("텍스트", "Văn bản")} onClick={() => handleAddBlock('text')} color="bg-gray-100 text-gray-600" />
                                    <BlockOption icon={<Music2 />} label="음악" onClick={() => handleAddBlock('music')} color="bg-red-100 text-red-600" />
                                </div>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-500 mb-3">{tx("커뮤니케이션", "Liên lạc")}</h4>
                                <div className="grid grid-cols-4 gap-4 md:gap-6">
                                    <BlockOption icon={<Instagram />} label={tx("SNS 연결", "Kết nối SNS")} onClick={() => handleAddBlock('sns')} color="bg-pink-100 text-pink-600" />
                                    <BlockOption icon={<Sparkles />} label="AI 답변" onClick={() => handleAddBlock('ai')} color="bg-indigo-100 text-indigo-600" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    const BlockOption = ({ icon, label, onClick, color }) => (
        <button onClick={onClick} className="flex flex-col items-center gap-2 group">
            <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center text-2xl transition-transform group-hover:scale-110 shadow-sm`}>
                {React.cloneElement(icon, { size: 28 })}
            </div>
            <span className="text-xs font-medium text-gray-700">{label}</span>
        </button>
    );

    return (
        <div className="bg-[#111] min-h-screen">
            <SaveStatusIndicator status={saveStatus} setStatus={setSaveStatus} />
            <AnimatePresence>
                {cropperOpen && (
                    <div className="fixed inset-0 z-[1100] bg-black/90 flex flex-col items-center justify-center p-4">
                        <div className="relative w-full max-w-lg h-[60vh] bg-gray-800 rounded-xl overflow-hidden">
                            <Cropper
                                image={imageToCrop} crop={crop} zoom={zoom} aspect={cropShape === 'round' ? 1 : 16 / 9}
                                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} cropShape={cropShape}
                            />
                        </div>
                        <div className="mt-4 flex gap-4">
                            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="range-sm" />
                            <button onClick={handleCropApply} className="px-6 py-2 bg-indigo-600 text-white rounded-lg">{tx("적용", "Áp dụng")}</button>
                            <button onClick={() => setCropperOpen(false)} className="px-6 py-2 bg-gray-600 text-white rounded-lg">{tx("취소", "Hủy")}</button>
                        </div>
                    </div>
                )}
            </AnimatePresence>

            <BlockSelectionModal />

            <div className="hidden min-h-[calc(100dvh-3.5rem)] pt-12 md:grid md:grid-cols-3 lg:grid-cols-2">
                <div className="min-h-full md:col-span-2 lg:col-span-1">
                    {renderEditor()}
                </div>
                <div className="flex min-h-full items-start justify-center bg-[#111216] p-6">
                    <div className="w-full max-w-[640px] overflow-hidden rounded-lg bg-white shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
                        <ProfilePreview profile={profile} onShareClick={() => setIsShareModalOpen(true)} locale={locale} displayMode="page" />
                    </div>
                </div>
            </div>

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                shareUrl={`${window.location.origin}/${locale}/${profile.unique_id}`}
                profile={profile}
            />

            <div className="md:hidden pt-12">
                {renderEditor()}
                <a
                    href={profile.unique_id ? `/${locale}/${profile.unique_id}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fixed bottom-20 right-5 z-40 bg-indigo-600 text-white p-4 rounded-full shadow-xl"
                >
                    <Smartphone />
                </a>
            </div>
        </div>
    );
}

const SaveStatusIndicator = ({ status, setStatus }) => {
    const [isVisible, setIsVisible] = useState(false);
    const isCopyableUrl =
        status.type === 'success' &&
        status.message &&
        status.message !== '삭제 완료' &&
        status.message !== 'Đã xóa';

    useEffect(() => {
        if (status.type !== 'idle') {
            setIsVisible(true);
            const duration = status.type === 'success' ? 5000 : 2000;
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => setStatus({ type: 'idle', message: '' }), 300);
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [status, setStatus]);

    const handleCopy = () => {
        if (status.type === 'success' && status.message) {
            const url = `https://disparty.com/${status.message}`;
            navigator.clipboard.writeText(url).then(() => {
                alert('Copied!');
            });
        }
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -50, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                    className={`fixed top-0 left-0 w-full z-[1100] shadow-md border-b
                        ${status.type === 'success' ? 'bg-indigo-600 border-indigo-500' :
                            status.type === 'error' ? 'bg-red-600 border-red-500' :
                                'bg-gray-800 border-gray-700'}
                    `}
                >
                    <div className="container mx-auto max-w-lg px-4 h-12 flex items-center justify-between text-white">
                        <div className="flex items-center gap-2 overflow-hidden">
                            {status.type === 'saving' && <Loader className="animate-spin w-4 h-4 shrink-0" />}
                            {status.type === 'success' && <Check className="w-4 h-4 shrink-0" />}
                            {status.type === 'error' && <AlertTriangle className="w-4 h-4 shrink-0" />}

                            {isCopyableUrl ? (
                                <span className="text-sm font-medium truncate">
                                    https://disparty.com/{status.message}
                                </span>
                            ) : (
                                <span className="text-sm font-medium truncate">{status.message}</span>
                            )}
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                            {isCopyableUrl && (
                                <button
                                    onClick={handleCopy}
                                    className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors"
                                >
                                    <Copy className="w-3 h-3" />
                                    Copy
                                </button>
                            )}
                            <button
                                onClick={() => setIsVisible(false)}
                                className="p-1 hover:bg-black/20 rounded-full transition-colors"
                            >
                                <LucideX className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

function DesignPage() {
    const location = useLocation();
    const locale = getLocaleFromPathname(location.pathname);
    const initialSection = new URLSearchParams(location.search).get('section');
    const [designSection, setDesignSection] = useState(
        locale === 'ko' && initialSection === 'server-card' ? 'server-card' : 'profile'
    );

    const changeSection = (section) => {
        setDesignSection(section);
        const url = new URL(window.location.href);
        if (section === 'server-card') url.searchParams.set('section', 'server-card');
        else url.searchParams.delete('section');
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
    };

    return (
        <div className="min-h-screen bg-[#111216]">
            {locale === 'ko' && (
                <div className="relative z-[1000] border-b border-[#30323a] bg-[#17181c] px-4">
                    <div className="mx-auto flex h-14 max-w-7xl items-center justify-center gap-2">
                        <button
                            type="button"
                            onClick={() => changeSection('profile')}
                            className={`h-10 rounded-lg px-5 text-sm font-bold transition-colors ${designSection === 'profile' ? 'bg-[#5865F2] text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                        >
                            프로필 디자인
                        </button>
                        <button
                            type="button"
                            onClick={() => changeSection('server-card')}
                            className={`h-10 rounded-lg px-5 text-sm font-bold transition-colors ${designSection === 'server-card' ? 'bg-[#5865F2] text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                        >
                            서버 카드 디자인
                        </button>
                    </div>
                </div>
            )}
            {designSection === 'server-card' && locale === 'ko' ? <ServerCardStudio /> : <ProfileDesignPage />}
        </div>
    );
}

export default DesignPage;
